import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, MapPin, Plus, Search, X } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";

const labelClass =
  "block text-[10px] uppercase tracking-[0.16em] text-[#666660] mb-1.5";
const lightInputClass =
  "text-[14px] text-[#1A1A1A] placeholder:text-[#666660] font-light pr-6 bg-transparent";
const contactInputClass =
  "w-full min-h-[44px] px-4 py-3 bg-white border border-[#E5E0D8] text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:border-[#1A1A1A] focus:ring-1 focus:ring-[#1A1A1A]/20 text-sm font-light";

/**
 * Warm-ivory floating search / enquiry panel for the homepage hero.
 * Desktop: floating panel. Mobile: compact entry + accessible bottom sheet.
 */
const HeroSearchPanel = ({
  language,
  t,
  searchMode,
  setSearchMode,
  filters,
  setFilters,
  contact,
  setContact,
  formStep,
  setFormStep,
  showMoreOptions,
  setShowMoreOptions,
  submitting,
  locationOptions,
  priceRanges,
  surfaceOptions,
  bedroomOptions,
  propertyTypeOptions,
  onCriteriaContinue,
  onEnquirySubmit,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const titleId = useId();
  const triggerRef = useRef(null);
  const sheetCloseRef = useRef(null);

  const closeMobileSheet = () => {
    setMobileOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  // Escape + body scroll lock for the mobile search sheet (z above cookie banner)
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMobileSheet();
      }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    window.setTimeout(() => sheetCloseRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  const searchLabel = language === "fr" ? "Rechercher" : "Search";
  const moreLabel = t("home.moreOptions");
  const buyLabel = t("home.buy");
  const rentLabel = t("home.rent");

  const modeToggle = (compact = false) => (
    <div
      className={`flex items-center ${compact ? "gap-3 justify-center" : "gap-1"}`}
      role="group"
      aria-label={language === "fr" ? "Type de recherche" : "Search type"}
    >
      <button
        type="button"
        onClick={() => setSearchMode("buy")}
        aria-pressed={searchMode === "buy"}
        className={`min-h-[44px] px-2 text-[11px] uppercase tracking-[0.18em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A] ${
          searchMode === "buy"
            ? "text-[#1A1A1A] border-b border-[#1A1A1A]"
            : "text-[#666660] hover:text-[#1A1A1A]"
        }`}
        data-testid={compact ? "search-mode-buy-mobile" : "search-mode-buy"}
      >
        {buyLabel}
      </button>
      <span className="text-[#C8C2B8] text-xs" aria-hidden="true">
        /
      </span>
      <button
        type="button"
        onClick={() => setSearchMode("rent")}
        aria-pressed={searchMode === "rent"}
        className={`min-h-[44px] px-2 text-[11px] uppercase tracking-[0.18em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A] ${
          searchMode === "rent"
            ? "text-[#1A1A1A] border-b border-[#1A1A1A]"
            : "text-[#666660] hover:text-[#1A1A1A]"
        }`}
        data-testid={compact ? "search-mode-rent-mobile" : "search-mode-rent"}
      >
        {rentLabel}
      </button>
    </div>
  );

  const moreFiltersRow = (mobile = false) => (
    <AnimatePresence initial={false}>
      {showMoreOptions && (
        <motion.div
          key="more-filters"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.2 }}
          className={mobile ? "pt-1 pb-3" : "border-t border-[#E8E3DA]"}
          data-testid={mobile ? "more-filters-mobile" : "more-filters-desktop"}
        >
          <div
            className={
              mobile
                ? "grid grid-cols-2 gap-3"
                : "flex items-stretch divide-x divide-[#E8E3DA]"
            }
          >
            <div className={mobile ? "" : "flex items-end gap-2 px-6 py-3 min-w-[160px] flex-1"}>
              <div className="flex-1">
                <label className={labelClass}>{t("home.anyBedrooms")}</label>
                <SearchableSelect
                  value={filters.bedrooms}
                  onChange={(val) => setFilters({ ...filters, bedrooms: val })}
                  options={bedroomOptions}
                  placeholder={t("home.anyBedrooms")}
                  inputClassName={lightInputClass}
                  variant="light"
                  data-testid={mobile ? "search-bedrooms-mobile" : "search-bedrooms"}
                />
              </div>
              {!mobile && (
                <span className="text-[#666660] text-[11px] uppercase tracking-wide flex-shrink-0 pb-1">
                  {t("home.beds")}
                </span>
              )}
            </div>
            <div className={mobile ? "" : "px-6 py-3 min-w-[180px] flex-1"}>
              <label className={labelClass}>{t("home.allTypes")}</label>
              <SearchableSelect
                value={filters.propertyType}
                onChange={(val) => setFilters({ ...filters, propertyType: val })}
                options={propertyTypeOptions}
                placeholder={t("home.allTypes")}
                inputClassName={lightInputClass}
                variant="light"
                data-testid={
                  mobile ? "search-property-type-mobile" : "search-property-type"
                }
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const contactFields = () => (
    <div>
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <p className="text-[#666660] text-sm font-light">
          {language === "fr" ? "Vos coordonnées" : "Your contact details"}
        </p>
        <button
          type="button"
          onClick={() => setFormStep("criteria")}
          className="min-h-[44px] text-[11px] uppercase tracking-[0.12em] text-[#666660] hover:text-[#1A1A1A] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]"
        >
          {language === "fr" ? "Modifier les critères" : "Edit criteria"}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          required
          autoComplete="given-name"
          placeholder={language === "fr" ? "Prénom" : "First name"}
          aria-label={language === "fr" ? "Prénom" : "First name"}
          value={contact.first_name}
          onChange={(e) => setContact({ ...contact, first_name: e.target.value })}
          className={contactInputClass}
          data-testid="hero-enquiry-first-name"
        />
        <input
          type="text"
          required
          autoComplete="family-name"
          placeholder={language === "fr" ? "Nom" : "Last name"}
          aria-label={language === "fr" ? "Nom" : "Last name"}
          value={contact.last_name}
          onChange={(e) => setContact({ ...contact, last_name: e.target.value })}
          className={contactInputClass}
          data-testid="hero-enquiry-last-name"
        />
        <input
          type="email"
          required
          autoComplete="email"
          placeholder="Email"
          aria-label="Email"
          value={contact.email}
          onChange={(e) => setContact({ ...contact, email: e.target.value })}
          className={contactInputClass}
          data-testid="hero-enquiry-email"
        />
        <input
          type="tel"
          required
          autoComplete="tel"
          placeholder={language === "fr" ? "Téléphone" : "Phone"}
          aria-label={language === "fr" ? "Téléphone" : "Phone"}
          value={contact.phone}
          onChange={(e) => setContact({ ...contact, phone: e.target.value })}
          className={contactInputClass}
          data-testid="hero-enquiry-phone"
        />
      </div>
      <textarea
        rows={3}
        placeholder={language === "fr" ? "Message (optionnel)" : "Message (optional)"}
        aria-label={language === "fr" ? "Message (optionnel)" : "Message (optional)"}
        value={contact.message}
        onChange={(e) => setContact({ ...contact, message: e.target.value })}
        className={`${contactInputClass} mb-4 resize-none`}
        data-testid="hero-enquiry-message"
      />
      <button
        type="submit"
        disabled={submitting}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[48px] px-8 py-3 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white text-[12px] uppercase tracking-[0.15em] transition-colors disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]"
        data-testid="hero-enquiry-submit"
      >
        <span>
          {submitting
            ? language === "fr"
              ? "Envoi..."
              : "Sending..."
            : language === "fr"
              ? "Envoyer ma demande"
              : "Send enquiry"}
        </span>
        {!submitting && <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />}
      </button>
    </div>
  );

  const successBlock = (
    <div className="px-6 py-10 md:px-8 md:py-12 text-center" data-testid="search-bar-success">
      <div className="w-12 h-12 rounded-full bg-[#1A1A1A]/8 text-[#1A1A1A] flex items-center justify-center mx-auto mb-5">
        <Check size={22} strokeWidth={1.5} aria-hidden="true" />
      </div>
      <h3 className="text-[#1A1A1A] text-xl md:text-2xl font-heading mb-3">
        {language === "fr" ? "Merci pour votre demande" : "Thank you for your enquiry"}
      </h3>
      <p className="text-[#666660] text-sm font-light max-w-md mx-auto">
        {language === "fr"
          ? "Notre équipe vous recontactera sous peu avec une sélection adaptée à vos critères."
          : "Our team will get back to you shortly with a selection matching your criteria."}
      </p>
    </div>
  );

  const panelClass =
    "bg-[#F7F3EC] border border-[#E8E3DA] shadow-[0_12px_40px_rgba(26,26,26,0.12)] rounded-sm overflow-visible";

  const desktopPanel = (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="hidden lg:block w-full max-w-5xl mx-auto"
    >
      {formStep === "success" ? (
        <div className={panelClass}>{successBlock}</div>
      ) : formStep === "contact" ? (
        <form
          onSubmit={onEnquirySubmit}
          className={`${panelClass} p-6 md:p-8`}
          data-testid="search-bar-contact"
        >
          {contactFields()}
        </form>
      ) : (
        <form
          onSubmit={onCriteriaContinue}
          className={panelClass}
          data-testid="search-bar"
        >
          <div className="flex items-stretch divide-x divide-[#E8E3DA]">
            <div className="flex items-center px-5 flex-shrink-0">
              {modeToggle(false)}
            </div>

            <div className="px-5 py-3.5 flex-1 min-w-[160px]">
              <label className={labelClass}>
                {language === "fr" ? "Localisation" : "Location"}
              </label>
              <SearchableSelect
                value={filters.location}
                onChange={(val) => setFilters({ ...filters, location: val })}
                options={locationOptions}
                placeholder={t("home.locationPlaceholder")}
                icon={MapPin}
                inputClassName={lightInputClass}
                variant="light"
                data-testid="search-location"
              />
            </div>

            <div className="px-5 py-3.5 min-w-[140px]">
              <label className={labelClass}>
                {language === "fr" ? "Budget" : "Budget"}
              </label>
              <SearchableSelect
                value={filters.maxPrice}
                onChange={(val) => setFilters({ ...filters, maxPrice: val })}
                options={priceRanges}
                placeholder="Budget"
                inputClassName={lightInputClass}
                variant="light"
                data-testid="search-max-price"
              />
            </div>

            <div className="px-5 py-3.5 min-w-[130px]">
              <label className={labelClass}>{t("home.minSurface")}</label>
              <SearchableSelect
                value={filters.minSurface}
                onChange={(val) => setFilters({ ...filters, minSurface: val })}
                options={surfaceOptions}
                placeholder={t("home.minSurface")}
                inputClassName={lightInputClass}
                variant="light"
                data-testid="search-min-surface"
              />
            </div>

            <button
              type="button"
              onClick={() => setShowMoreOptions(!showMoreOptions)}
              aria-expanded={showMoreOptions}
              className="flex items-center justify-center gap-2 px-5 min-h-[48px] text-[#666660] hover:text-[#1A1A1A] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A] flex-shrink-0"
              data-testid="more-options-toggle"
            >
              <Plus
                size={14}
                className={`transition-transform duration-200 ${showMoreOptions ? "rotate-45" : ""}`}
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <span className="text-[11px] uppercase tracking-[0.15em]">{moreLabel}</span>
            </button>

            <button
              type="submit"
              className="flex items-center justify-center gap-2 min-h-[52px] px-8 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white text-[11px] uppercase tracking-[0.15em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white flex-shrink-0"
              data-testid="search-submit"
            >
              <Search size={14} strokeWidth={1.5} aria-hidden="true" />
              <span>{searchLabel}</span>
            </button>
          </div>
          {moreFiltersRow(false)}
        </form>
      )}
    </motion.div>
  );

  const mobileEntry = (
    <div className="lg:hidden w-full max-w-md mx-auto">
      {formStep === "success" ? (
        <div className={panelClass}>{successBlock}</div>
      ) : (
        <>
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setMobileOpen(true)}
            className="w-full flex items-center justify-between gap-3 min-h-[52px] px-5 py-3 bg-[#F7F3EC] border border-[#E8E3DA] shadow-[0_10px_32px_rgba(26,26,26,0.14)] rounded-sm text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]"
            data-testid="search-mobile-trigger"
            aria-haspopup="dialog"
            aria-expanded={mobileOpen}
          >
            <span className="flex items-center gap-3 min-w-0">
              <Search
                size={16}
                className="text-[#1A1A1A] flex-shrink-0"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <span className="truncate">
                <span className="block text-[10px] uppercase tracking-[0.16em] text-[#666660]">
                  {searchMode === "buy" ? buyLabel : rentLabel}
                </span>
                <span className="block text-[14px] text-[#1A1A1A] font-light truncate">
                  {filters.location || t("home.locationPlaceholder")}
                </span>
              </span>
            </span>
            <span className="flex-shrink-0 text-[11px] uppercase tracking-[0.14em] text-white bg-[#1A1A1A] px-3 py-2">
              {searchLabel}
            </span>
          </button>

          {mobileOpen &&
            typeof document !== "undefined" &&
            createPortal(
              <div
                className="fixed inset-0 z-[90] lg:hidden"
                data-testid="search-mobile-sheet"
              >
                <button
                  type="button"
                  className="absolute inset-0 bg-black/45 border-0 cursor-default"
                  aria-label={language === "fr" ? "Fermer" : "Close"}
                  onClick={closeMobileSheet}
                />
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={titleId}
                  className="absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto bg-[#F7F3EC] border-t border-[#E8E3DA] rounded-t-md shadow-[0_-12px_40px_rgba(26,26,26,0.18)]"
                >
                  <div className="px-5 pt-5 pb-3 border-b border-[#E8E3DA]">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2
                          id={titleId}
                          className="font-heading text-[#1A1A1A] text-xl font-normal"
                        >
                          {searchLabel}
                        </h2>
                        <p className="text-[#666660] text-sm font-light mt-1">
                          {language === "fr"
                            ? "Précisez vos critères — notre équipe vous recontactera."
                            : "Share your criteria — our team will follow up."}
                        </p>
                      </div>
                      <button
                        ref={sheetCloseRef}
                        type="button"
                        onClick={closeMobileSheet}
                        className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-[#666660] hover:text-[#1A1A1A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]"
                        aria-label={language === "fr" ? "Fermer" : "Close"}
                        data-testid="search-sheet-close"
                      >
                        <X size={20} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>

                  <div className="px-5 py-4">
                    {formStep === "contact" ? (
                      <form
                        onSubmit={onEnquirySubmit}
                        data-testid="search-bar-contact"
                      >
                        {contactFields()}
                      </form>
                    ) : (
                      <form
                        onSubmit={onCriteriaContinue}
                        data-testid="search-bar"
                      >
                        <div className="pb-4 border-b border-[#E8E3DA] mb-4">
                          {modeToggle(true)}
                        </div>

                        <div className="space-y-4 mb-3">
                          <div>
                            <label className={labelClass}>
                              {language === "fr" ? "Localisation" : "Location"}
                            </label>
                            <SearchableSelect
                              value={filters.location}
                              onChange={(val) =>
                                setFilters({ ...filters, location: val })
                              }
                              options={locationOptions}
                              placeholder={t("home.locationPlaceholder")}
                              icon={MapPin}
                              inputClassName={lightInputClass}
                              variant="light"
                              data-testid="search-location-mobile"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={labelClass}>
                                {language === "fr" ? "Budget" : "Budget"}
                              </label>
                              <SearchableSelect
                                value={filters.maxPrice}
                                onChange={(val) =>
                                  setFilters({ ...filters, maxPrice: val })
                                }
                                options={priceRanges}
                                placeholder="Budget"
                                inputClassName={lightInputClass}
                                variant="light"
                                data-testid="search-max-price-mobile"
                              />
                            </div>
                            <div>
                              <label className={labelClass}>
                                {t("home.minSurface")}
                              </label>
                              <SearchableSelect
                                value={filters.minSurface}
                                onChange={(val) =>
                                  setFilters({ ...filters, minSurface: val })
                                }
                                options={surfaceOptions}
                                placeholder={t("home.minSurface")}
                                inputClassName={lightInputClass}
                                variant="light"
                                data-testid="search-min-surface-mobile"
                              />
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              setShowMoreOptions(!showMoreOptions)
                            }
                            aria-expanded={showMoreOptions}
                            className="w-full flex items-center justify-center gap-2 min-h-[44px] border border-[#E8E3DA] text-[#666660] hover:text-[#1A1A1A] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A1A1A]"
                            data-testid="more-options-toggle-mobile"
                          >
                            <Plus
                              size={14}
                              className={`transition-transform duration-200 ${showMoreOptions ? "rotate-45" : ""}`}
                              strokeWidth={1.5}
                              aria-hidden="true"
                            />
                            <span className="text-[11px] uppercase tracking-[0.15em]">
                              {moreLabel}
                            </span>
                          </button>

                          {moreFiltersRow(true)}
                        </div>

                        <button
                          type="submit"
                          className="w-full flex items-center justify-center gap-2 min-h-[48px] mt-2 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white text-[12px] uppercase tracking-[0.15em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                          data-testid="search-submit-mobile"
                        >
                          <Search
                            size={14}
                            strokeWidth={1.5}
                            aria-hidden="true"
                          />
                          <span>{searchLabel}</span>
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  );

  return (
    <div className="w-full px-4 md:px-8" data-testid="hero-search-panel">
      {desktopPanel}
      {mobileEntry}
    </div>
  );
};

export default HeroSearchPanel;
